import { Target, Rocket, Boxes, ChartLine } from "lucide-react";

const t = {
  fr: {
    title: "Vision à Long Terme",
    subtitle: "De Leader Régional à Norme Nationale",
    phases: [
      { icon: Target, badge: "Phase 1", title: "Domination au Québec", time: "12–18 mois", desc: "Standardiser l'expérience des repas scolaires à travers la province" },
      { icon: Rocket, badge: "Phase 2", title: "Expansion au Canada", time: "12–36 mois", desc: "Réplication facile grâce à la conception de plateforme unifiée" },
      { icon: Boxes, badge: "Phase 3", title: "Ascension Verticale", time: "6 mois", desc: "Contrats CSS directs, camps d'été, garderies, programmes parascolaires" },
      { icon: ChartLine, badge: "Phase 4", title: "Sky is the Limit!", time: "36 mois", desc: "Programmes entreprise, hôpitaux, centres communautaires, expansion internationale" },
    ],
    whyWins: "Pourquoi EcoLunch Gagne",
    pillars: [
      "Unifié (pas fragmenté)", "Reconnaissable (axé sur la marque)", "Facile à adopter (intégration simple)", "Fiable pour les parents",
      "Apprécié par les écoles", "Alimenté par les traiteurs", "Conçu pour l'expansion", "Cohérent et moderne",
    ],
    conclusion: "Il résout un problème qui touche des millions de parents et des milliers d'institutions — et le fait avec un modèle qui croît organiquement grâce à l'utilisation quotidienne.",
  },
  en: {
    title: "Long-Term Vision",
    subtitle: "From Regional Leader to National Standard",
    phases: [
      { icon: Target, badge: "Phase 1", title: "Quebec Domination", time: "12–18 months", desc: "Standardize the school meal experience across the province" },
      { icon: Rocket, badge: "Phase 2", title: "Canada Expansion", time: "12–36 months", desc: "Easy replication through unified platform design" },
      { icon: Boxes, badge: "Phase 3", title: "Vertical Ascension", time: "6 months", desc: "Direct CSS contracts, summer camps, daycares, after-school programs" },
      { icon: ChartLine, badge: "Phase 4", title: "Sky is the Limit!", time: "36 months", desc: "Corporate programs, hospitals, community centers, international expansion" },
    ],
    whyWins: "Why EcoLunch Wins",
    pillars: [
      "Unified (not fragmented)", "Recognizable (brand-driven)", "Easy to adopt (simple onboarding)", "Trusted by parents",
      "Valued by schools", "Powered by caterers", "Built for expansion", "Consistent and modern",
    ],
    conclusion: "It solves a problem that affects millions of parents and thousands of institutions — and does it with a model that grows organically through daily usage.",
  },
};

export default function VisionSection({ lang }) {
  const c = t[lang];
  return (
    <section id="vision" data-testid="vision-section" className="py-16 sm:py-24 bg-darker">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/20 mb-6 glow-strong">
            <Target className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ textShadow: "0 0 30px rgba(182,236,19,0.4)" }}>{c.title}</h2>
          <p className="text-lg sm:text-xl text-gradient-lime-blue font-bold px-4">{c.subtitle}</p>
        </div>
        <div className="max-w-5xl mx-auto mb-16 sm:mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {c.phases.map((phase) => {
              const Icon = phase.icon;
              return (
                <div key={phase.badge} className="rounded-xl border text-card-foreground shadow hover-lift bg-dark border-primary/30 hover:border-accent transition-all duration-300 glow-blue-soft">
                  <div className="p-6 pt-6 sm:pt-8 pb-6 sm:pb-8">
                    <div className="flex items-start gap-3 sm:gap-4 mb-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary/20 flex-shrink-0 glow-primary">
                        <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="inline-flex items-center rounded-md border px-2.5 py-0.5 shadow mb-2 bg-primary/20 text-primary border-primary/30 font-bold text-xs sm:text-sm">{phase.badge}</div>
                        <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">{phase.title}</h3>
                        <p className="text-xs sm:text-sm text-white/60 mb-3 font-semibold">{phase.time}</p>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base text-white/70">{phase.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 px-4" style={{ textShadow: "0 0 25px rgba(182,236,19,0.5)" }}>{c.whyWins}</h3>
          </div>
          <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/50 mb-8 glow-blue-soft">
            <div className="p-6 py-6 sm:py-8">
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {c.pillars.map((p) => (
                  <div key={p} className="bg-primary/20 rounded-lg p-3 sm:p-4 text-center border border-primary/40">
                    <p className="text-xs sm:text-sm font-black text-white">{p}</p>
                  </div>
                ))}
              </div>
              <p className="text-base sm:text-lg leading-relaxed font-semibold text-white px-4">{c.conclusion}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
